// src/pages/CreatorTripPage.jsx
// routechef.com/c/:handle/:slug  ·  one creator trip, bookable from any city.
// The follower answers 3 things (from, date, travellers). Everything else is
// the creator's template; getting there / back, stays and price fill live.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { FEATURES } from "../config/features";
import { getCreator, getTrip, ORIGIN_CITIES } from "../creator/creatorTripsData";
import {
    buildInstance, repriceInstance, addDays, fmtDay, fmtTime, inr, toISO,
    routeArr, routeDep, routeModes, routeModeKey, segMode, segName, PLATFORM_FEE, PLATFORM_GST,
} from "../creator/creatorTripEngine";
import { openInNewTab } from "../utils/bookingTab";
import { Map as MapIcon } from "lucide-react";
// Existing route map modal (same one the search results use). Adjust the path if yours differs.
import RouteMapModal from "../components/modal/RouteMapModal";

const ORANGE = "#FF5B15";
const INK = "#0F172A";
const MUTED = "#64748B";
const GREEN = "#059669";
const AMBER = "#B45309";

const MODE_ICON = { flight: "✈️", bus: "🚌", train: "🚆", cab: "🚕", shared_cab: "🚐" };
const BLOCK_ICON = { STOP: "📍", EXPERIENCE: "✨", STAY: "🏨", TIP: "💡", MOVE: "🧭" };
const LOCK_LABEL = { LOCKED: "Creator pick", PREFERRED: "Preferred", FLEXIBLE: "Flexible" };

export default function CreatorTripPage() {
    const { handle, slug } = useParams();
    const navigate = useNavigate();
    const trip = getTrip(slug);
    const creator = getCreator(handle);

    const [origin, setOrigin] = useState(ORIGIN_CITIES[0]);
    const [startDate, setStartDate] = useState(() => toISO(addDays(new Date(), 5)));
    const [pax, setPax] = useState(2);
    const [routePref, setRoutePref] = useState("best");
    const [baseInst, setInst] = useState(null);
    const [outIdx, setOutIdx] = useState(0);
    const [retIdx, setRetIdx] = useState(0);
    // Swapping a getting-there / back option re-prices locally, no new search
    const inst = useMemo(() => (baseInst ? repriceInstance(baseInst, outIdx, retIdx) : null), [baseInst, outIdx, retIdx]);
    const [loading, setLoading] = useState(true);
    const [progress, setProgress] = useState("");
    const [checkout, setCheckout] = useState(false);
    const reqRef = useRef(0);

    useEffect(() => {
        if (!trip) return;
        const id = ++reqRef.current;
        setLoading(true);
        const t = setTimeout(async () => {
            const res = await buildInstance(trip, {
                origin, startDate: new Date(startDate + "T00:00:00"), pax, routePref,
                live: FEATURES.CREATOR_TRIPS_LIVE_SEARCH,
                onProgress: (p) => id === reqRef.current && setProgress(p),
            });
            if (id !== reqRef.current) return; // a newer request won
            setInst(res);
            setOutIdx(0);
            setRetIdx(0);
            setLoading(false);
        }, 250);
        return () => clearTimeout(t);
    }, [trip, origin, startDate, pax, routePref]);

    if (!FEATURES.ENABLE_CREATOR_TRIPS || !trip || !creator) return <Navigate to="/" replace />;

    const day1 = new Date(startDate + "T00:00:00");

    return (
        <div style={S.page}>
            <style>{CSS}</style>

            {/* Top bar */}
            <header style={S.top}>
                <Link to={`/c/${handle}`} style={S.back}>← @{creator.handle}</Link>
                <Link to="/" style={{ textDecoration: "none", fontSize: 17 }}>
                    <span style={{ color: ORANGE, fontWeight: 700 }}>Route</span><span style={{ color: INK }}>chef</span>
                </Link>
            </header>

            {/* Hero */}
            <section style={{ ...S.hero, backgroundImage: `linear-gradient(180deg, rgba(15,23,42,.15) 0%, rgba(15,23,42,.85) 100%), url(${trip.cover})` }}>
                <div style={S.heroInner}>
                    <div style={S.creatorChip}>
                        <span style={S.miniAvatar}>{creator.avatar_initials}</span>
                        Trip by {creator.name} · {creator.followers} followers
                    </div>
                    <h1 style={{ color: "#fff", fontSize: "clamp(26px, 4.5vw, 38px)", margin: "10px 0 6px", lineHeight: 1.1 }}>{trip.title}</h1>
                    <div style={{ color: "rgba(255,255,255,.85)", fontSize: 15 }}>{trip.subtitle}</div>
                    <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap", alignItems: "center" }}>
                        <a href={trip.reel_url} target="_blank" rel="noopener noreferrer" style={S.reelBtn}>▶ Watch the reel · {trip.reel_views}</a>
                        {trip.vibe.map((v) => <span key={v} style={S.heroTag}>{v}</span>)}
                    </div>
                </div>
            </section>

            {/* The 3 questions */}
            <div style={S.controlsWrap}>
                <div style={S.controls}>
                    <Field label="Travelling from">
                        <select
                            value={origin.name}
                            onChange={(e) => setOrigin(ORIGIN_CITIES.find((c) => c.name === e.target.value))}
                            style={S.input}
                        >
                            {ORIGIN_CITIES.map((c) => <option key={c.name}>{c.name}</option>)}
                        </select>
                    </Field>
                    <Field label={`Day 1 in ${trip.entry.city}`}>
                        <input type="date" value={startDate} min={toISO(addDays(new Date(), 1))} onChange={(e) => e.target.value && setStartDate(e.target.value)} style={S.input} />
                    </Field>
                    <Field label="Travellers">
                        <div style={{ display: "flex", alignItems: "center", gap: 10, height: 40 }}>
                            <button style={S.step} onClick={() => setPax((p) => Math.max(1, p - 1))} aria-label="Fewer travellers">−</button>
                            <b style={{ minWidth: 18, textAlign: "center" }}>{pax}</b>
                            <button style={S.step} onClick={() => setPax((p) => Math.min(trip.max_group, p + 1))} aria-label="More travellers">+</button>
                        </div>
                    </Field>
                    <Field label="Optimise for">
                        <div style={{ display: "flex", gap: 4, height: 40, alignItems: "center" }}>
                            {[["best", "Best"], ["cheapest", "Cheapest"], ["fastest", "Fastest"]].map(([k, l]) => (
                                <button key={k} onClick={() => setRoutePref(k)} style={{ ...S.chip, ...(routePref === k ? S.chipOn : null) }}>{l}</button>
                            ))}
                        </div>
                    </Field>
                </div>
                <div style={S.status}>
                    {loading
                        ? <span className="ct-pulse">● {progress || "Re-planning"}…</span>
                        : inst && <span>
                            <b style={{ color: GREEN }}>✓</b> Re-planned from <b>{origin.name}</b> in {(inst.ms / 1000).toFixed(1)}s
                            {inst.anyEstimated ? <span style={{ color: AMBER }}> · some prices estimated</span> : <span style={{ color: GREEN }}> · live prices</span>}
                        </span>}
                </div>
            </div>

            <main style={S.main} className="ct-main">
                {/* Timeline */}
                <div style={{ minWidth: 0, opacity: loading ? 0.55 : 1, transition: "opacity .2s" }}>
                    {inst?.warnings.map((w, i) => (
                        <div key={i} style={{ ...S.warn, ...(w.kind === "season" ? S.warnSeason : null) }}>
                            {w.kind === "season" ? "⚠️ " : "🗓 "}{w.text}
                        </div>
                    ))}

                    {/* Getting there */}
                    <Section title="Getting there" sub={`${origin.name} → ${trip.entry.name}`} badge="Live for you">
                        {inst && <EdgeLeg edge={inst.outbound} options={inst.outOptions} onPick={setOutIdx} from={origin} to={{ name: trip.entry.city, lat: trip.entry.lat, lng: trip.entry.lng }} cityA={origin.name} cityB={trip.entry.city} pax={pax} startBy={trip.entry.start_by} />}
                        {inst?.outbound.needsArrivalNight && (
                            <div style={S.subNote}>🏨 Extra night in {trip.entry.city} before Day 1 (estimated, picked at checkout)</div>
                        )}
                    </Section>

                    {/* Days */}
                    {trip.days.map((d) => (
                        <Section key={d.day} title={`Day ${d.day} · ${d.title}`} sub={fmtDay(addDays(day1, d.day - 1))}>
                            {d.blocks.map((b, i) => (
                                <Block key={i} b={b} pax={pax} stay={b.type === "STAY" ? inst?.stays.find((s) => s.block.dayIndex === d.day - 1 && s.block.city === b.city) : null} />
                            ))}
                        </Section>
                    ))}

                    {/* Getting back */}
                    <Section title="Getting back" sub={`${trip.exit.name} → ${origin.name} · ${fmtDay(addDays(day1, trip.exit.day_offset))}`} badge="Live for you">
                        {inst && <EdgeLeg edge={inst.inbound} options={inst.retOptions} onPick={setRetIdx} from={{ name: trip.exit.city, lat: trip.exit.lat, lng: trip.exit.lng }} to={origin} cityA={trip.exit.city} cityB={origin.name} pax={pax} />}
                    </Section>

                    <div style={S.legend}>
                        <span><b>Creator pick</b> never changes</span>
                        <span><b>Preferred</b> booked if available, else same tier nearby</span>
                        <span><b>Flexible</b> best value picked for your dates</span>
                    </div>
                </div>

                {/* Price card */}
                <aside style={S.priceCol}>
                    <div style={S.priceCard}>
                        <div style={{ fontSize: 12, color: MUTED, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1 }}>Your trip from {origin.name}</div>
                        <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 6 }}>
                            <div style={{ fontSize: 34, fontWeight: 900, color: INK }}>{inst ? inr(inst.price.perPerson) : "…"}</div>
                            <div style={{ color: MUTED, fontSize: 13 }}>per person</div>
                        </div>
                        <div style={{ color: MUTED, fontSize: 13 }}>{inst ? `${inr(inst.price.total)} total · ${pax} traveller${pax > 1 ? "s" : ""} · ${inst.rooms} room${inst.rooms > 1 ? "s" : ""}` : "Calculating"}</div>

                        {inst && (
                            <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 7 }}>
                                {inst.price.lines.map((l) => (
                                    <div key={l.k} style={S.line}>
                                        <span style={S.lineLabel}>{l.label}{l.estimated && <span style={S.est}>est.</span>}</span>
                                        <span>{inr(l.amount)}</span>
                                    </div>
                                ))}
                                <div style={S.line}><span style={S.lineLabel}>Itinerary, support and fees</span><span>{inr(inst.price.markup + inst.price.fees)}</span></div>
                                <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>Curated by @{creator.handle} · on-trip WhatsApp support</div>
                            </div>
                        )}

                        <button disabled={!inst || loading} onClick={() => setCheckout(true)} style={{ ...S.bookBtn, opacity: !inst || loading ? 0.5 : 1 }}>
                            Book this trip →
                        </button>
                        <div style={{ fontSize: 11, color: MUTED, textAlign: "center", marginTop: 8 }}>Pay 25% now, rest 7 days before travel</div>
                    </div>
                </aside>
            </main>

            {/* Mobile sticky bar */}
            <div className="ct-mobilebar" style={S.mobileBar}>
                <div>
                    <div style={{ fontWeight: 900, fontSize: 20 }}>{inst ? inr(inst.price.perPerson) : "…"}</div>
                    <div style={{ fontSize: 11, color: MUTED }}>per person from {origin.name}</div>
                </div>
                <button disabled={!inst || loading} onClick={() => setCheckout(true)} style={{ ...S.bookBtn, width: "auto", marginTop: 0, padding: "12px 20px" }}>Book →</button>
            </div>

            {checkout && inst && (
                <Checkout inst={inst} trip={trip} creator={creator} pax={pax} onClose={() => setCheckout(false)} navigate={navigate} />
            )}
        </div>
    );
}

/* ── pieces ─────────────────────────────────────────────────────────────── */

const Field = ({ label, children }) => (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0, flex: "1 1 150px" }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.6 }}>{label}</span>
        {children}
    </label>
);

const Section = ({ title, sub, badge, children }) => (
    <section style={S.section}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
            <div>
                <div style={{ fontWeight: 800, fontSize: 16, color: INK }}>{title}</div>
                {sub && <div style={{ fontSize: 13, color: MUTED, marginTop: 2 }}>{sub}</div>}
            </div>
            {badge && <span style={S.liveBadge}>{badge}</span>}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>{children}</div>
    </section>
);

function EdgeLeg({ edge, options = [], onPick, from, to, cityA, cityB, pax, startBy }) {
    const [open, setOpen] = useState(false);
    const [listMode, setListMode] = useState("all");
    const [sortBy, setSortBy] = useState("best");
    const [mapRoute, setMapRoute] = useState(null);
    if (edge.skipped) {
        return <div style={S.subNote}>You're already in {cityB}. No travel needed.</div>;
    }
    const r = edge.route;
    const modes = routeModes(r);
    const modeKey = (o) => routeModeKey(o.route);
    const activeKey = routeModeKey(r);
    const tabLabel = (k) => k.split("+").map((m) => `${MODE_ICON[m] || ""} ${cap(m)}`).join(" + ");

    // One tab per mode combo found, in order of its best option
    const tabs = [];
    options.forEach((o, i) => {
        const k = modeKey(o);
        let t = tabs.find((x) => x.key === k);
        if (!t) { t = { key: k, bestIdx: i, minFare: o.fare, count: 0 }; tabs.push(t); }
        t.count += 1;
        if (o.fare < t.minFare) t.minFare = o.fare;
    });

    // "All routes" list: every option across modes, filterable and sortable
    const durMins = (o) => { const d = routeDep(o.route), a = routeArr(o.route); return d && a ? (new Date(a) - new Date(d)) / 60000 : 1e9; };
    const sorters = {
        best: null, // options already arrive ranked
        cheapest: (a, b) => a.o.fare - b.o.fare,
        fastest: (a, b) => durMins(a.o) - durMins(b.o),
        earliest: (a, b) => new Date(routeDep(a.o.route)) - new Date(routeDep(b.o.route)),
    };
    let shown = options.map((o, i) => ({ o, i })).filter(({ o }) => listMode === "all" || modeKey(o) === listMode);
    if (sorters[sortBy]) shown = [...shown].sort(sorters[sortBy]);

    const canMap = (route) => !route.estimated;
    const mapMeta = {
        user_src_ll: [from.lat, from.lng], user_dest_ll: [to.lat, to.lng],
        user_src_name: from.name, user_dest_name: to.name,
    };

    return (
        <div style={S.edgeCard}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "flex-start" }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: INK }}>
                    <span style={S.bestPick}>Best pick</span>
                    {modes.map((m) => MODE_ICON[m]).join(" + ")} {modes.map(cap).join(" + ")}
                    {edge.source === "estimate" && <span style={S.est}>estimated</span>}
                </div>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                    {canMap(r) && (
                        <button style={S.mapBtn} onClick={() => setMapRoute(r)} aria-label="Show this route on a map" title="Show on map">
                            <MapIcon size={16} />
                        </button>
                    )}
                    <div style={{ textAlign: "right" }}>
                        <div style={{ fontWeight: 800 }}>{inr(edge.fare)}</div>
                        <div style={{ fontSize: 11, color: MUTED }}>{pax} traveller{pax > 1 ? "s" : ""}</div>
                    </div>
                </div>
            </div>

            {tabs.length > 1 && (
                <div style={S.modeTabs} role="tablist" aria-label="Travel mode">
                    {tabs.map((t) => {
                        const on = t.key === activeKey;
                        return (
                            <button key={t.key} role="tab" aria-selected={on}
                                onClick={() => { if (!on) onPick(t.bestIdx); }}
                                style={{ ...S.modeTab, ...(on ? S.modeTabOn : null) }}>
                                <span style={{ fontWeight: 700, fontSize: 12 }}>{tabLabel(t.key)}</span>
                                <span style={{ fontSize: 11, color: on ? ORANGE : MUTED }}>from {inr(t.minFare)} · {t.count}</span>
                            </button>
                        );
                    })}
                </div>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 10 }}>
                {(r.nodes || []).map((n, i) => {
                    const seg = n.segment || {};
                    const m = segMode(seg);
                    return (
                        <div key={n.node_id || i} style={S.seg}>
                            <span style={{ fontSize: 16 }}>{MODE_ICON[m]}</span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 600, fontSize: 13 }}>{segName(seg)}</div>
                                <div style={{ fontSize: 12, color: MUTED }}>
                                    {fmtTime(seg.origin_dep_dt)} {seg.origin_name || cityA} → {fmtTime(seg.dest_reach_dt || seg.dest_arr_dt)} {seg.dest_name || cityB}
                                </div>
                            </div>
                            <span style={{ fontSize: 11, color: MUTED, whiteSpace: "nowrap" }}>{fmtDateShort(seg.origin_dep_dt)}</span>
                        </div>
                    );
                })}
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                {startBy && routeArr(r)
                    ? <div style={{ fontSize: 12, color: GREEN }}>✓ Reaches {fmtDateShort(routeArr(r))} {fmtTime(routeArr(r))}, trip starts {startBy}</div>
                    : <span />}
                {options.length > 1 && (
                    <button style={S.changeBtn} onClick={() => setOpen((v) => !v)}>
                        {open ? "Hide routes" : `See all ${options.length} routes`}
                    </button>
                )}
            </div>

            {open && (
                <div style={{ marginTop: 10 }}>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {[{ key: "all", count: options.length }, ...tabs].map((t) => (
                            <button key={t.key} onClick={() => setListMode(t.key)} style={{ ...S.chip, ...(listMode === t.key ? S.chipOn : null) }}>
                                {t.key === "all" ? "All" : tabLabel(t.key)} ({t.count})
                            </button>
                        ))}
                    </div>
                    <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.5 }}>Sort</span>
                        {[["best", "Best"], ["cheapest", "Cheapest"], ["fastest", "Fastest"], ["earliest", "Earliest"]].map(([k, l]) => (
                            <button key={k} onClick={() => setSortBy(k)} style={{ ...S.chip, padding: "4px 9px", ...(sortBy === k ? S.chipOn : null) }}>{l}</button>
                        ))}
                    </div>
                    <div style={S.optList}>
                        {shown.map(({ o, i }) => {
                            const on = i === edge.idx;
                            const dep = routeDep(o.route), arr = routeArr(o.route);
                            const mins = Math.round(durMins(o));
                            const diff = o.fare - edge.fare;
                            return (
                                <div key={i} role="button" tabIndex={0} style={{ ...S.opt, ...(on ? S.optOn : null) }}
                                    onClick={() => onPick(i)}
                                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onPick(i); } }}>
                                    <span style={{ fontSize: 15, whiteSpace: "nowrap" }}>{routeModes(o.route).map((m) => MODE_ICON[m]).join("")}</span>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontWeight: 600, fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                            {i === 0 && <span style={{ ...S.bestPick, marginRight: 6 }}>Best</span>}
                                            {(o.route.nodes || []).map((n) => segName(n.segment || {})).join(" + ")}
                                        </div>
                                        <div style={{ fontSize: 12, color: MUTED }}>
                                            {fmtDateShort(dep)} {fmtTime(dep)} → {fmtDateShort(arr)} {fmtTime(arr)} · {Math.floor(mins / 60)}h {mins % 60}m
                                            {(o.route.nodes || []).length > 1 ? ` · via ${o.route.nodes[0].segment?.dest_name || "1 stop"}` : ""}
                                            {o.needsNight ? " · +1 night before Day 1" : ""}
                                        </div>
                                    </div>
                                    {canMap(o.route) && (
                                        <button style={S.mapBtnSm} onClick={(e) => { e.stopPropagation(); setMapRoute(o.route); }} aria-label="Show on map" title="Show on map">
                                            <MapIcon size={14} />
                                        </button>
                                    )}
                                    <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                                        <div style={{ fontWeight: 800, fontSize: 13 }}>{inr(o.fare)}</div>
                                        <div style={{ fontSize: 11, color: on ? ORANGE : diff > 0 ? AMBER : GREEN }}>
                                            {on ? "Selected" : diff === 0 ? "same" : `${diff > 0 ? "+" : "−"}${inr(Math.abs(diff))}`}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <RouteMapModal
                isOpen={!!mapRoute}
                onClose={() => setMapRoute(null)}
                routeData={mapRoute}
                searchMetadata={mapMeta}
                title={`${from.name} to ${to.name}`}
            />
        </div>
    );
}

function Block({ b, pax, stay }) {
    if (b.type === "TIP") {
        return <div style={S.tip}>💡 {b.text}{b.reel_ts && <span style={{ color: MUTED }}> · reel {b.reel_ts}</span>}</div>;
    }
    if (b.type === "MOVE") {
        return (
            <div style={S.row}>
                <span style={S.rowIcon}>{MODE_ICON[b.mode] || "🧭"}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={S.rowTitle}>{b.from} → {b.to}<Lock l={b.lock} /></div>
                    <div style={S.rowSub}>{b.label} · dep {b.depart} · {b.duration}{b.overnight ? " · overnight, no hotel needed" : ""}</div>
                </div>
                <span style={S.rowPrice}>{inr(b.price_pp * pax)}</span>
            </div>
        );
    }
    if (b.type === "STAY") {
        const resolved = stay;
        return (
            <div style={{ ...S.row, background: "#FFFBF5", borderColor: "#FDE7D3" }}>
                <span style={S.rowIcon}>🏨</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={S.rowTitle}>{resolved ? resolved.name : (b.preferred || `Stay in ${b.city}`)}<Lock l={b.lock} /></div>
                    <div style={S.rowSub}>
                        {b.nights} night{b.nights > 1 ? "s" : ""} in {b.city}
                        {resolved && ` · ${resolved.rooms} room${resolved.rooms > 1 ? "s" : ""}`}
                        {resolved?.hotel?.matched === "preferred" && <span style={{ color: GREEN }}> · creator's hotel available</span>}
                        {resolved?.hotel?.matched === "substitute" && <span style={{ color: AMBER }}> · {b.preferred} unavailable, same tier nearby</span>}
                        {resolved?.estimated && <span style={{ color: AMBER }}> · estimated, confirmed at checkout</span>}
                        {resolved?.hotel?.room?.meal_type && resolved.hotel.room.meal_type !== "Room_Only" && " · breakfast incl."}
                    </div>
                </div>
                <span style={S.rowPrice}>{resolved ? inr(resolved.total) : "…"}</span>
            </div>
        );
    }
    return (
        <div style={S.row}>
            <span style={S.rowIcon}>{BLOCK_ICON[b.type]}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={S.rowTitle}>{b.name}<Lock l={b.lock} /></div>
                {(b.time || b.note) && <div style={S.rowSub}>{b.time}{b.time && b.note ? " · " : ""}{b.note}</div>}
            </div>
        </div>
    );
}

const Lock = ({ l }) => l ? <span style={{ ...S.lock, ...(l === "LOCKED" ? S.lockLocked : l === "PREFERRED" ? S.lockPref : S.lockFlex) }}>{LOCK_LABEL[l]}</span> : null;

/* ── checkout ───────────────────────────────────────────────────────────── */
function Checkout({ inst, trip, creator, pax, onClose, navigate }) {
    const [paid, setPaid] = useState(false);
    const deposit = Math.round(inst.price.total * 0.25);

    const bookHotel = (s) => {
        if (!s.hotel) return;
        const h = s.hotel;
        try {
            sessionStorage.setItem("rc_hotel_handoff_" + h.hotel_code, JSON.stringify({
                ...h, hotel_code: h.hotel_code, hotel_name: h.hotel_name,
                check_in: toISO(s.checkIn), check_out: toISO(s.checkOut), selectedRoom: h.room, images: h.images || [],
            }));
        } catch { /* storage blocked */ }
        let uid = "";
        try { uid = localStorage.getItem("routechef_user_id") || ""; } catch { /* blocked */ }
        navigate(`/trip-planner/hotel/${encodeURIComponent(h.hotel_code)}?check_in=${toISO(s.checkIn)}&check_out=${toISO(s.checkOut)}&user_id=${uid}`);
    };

    const bookBusSeg = (seg) => {
        openInNewTab(navigate, "/book-bus", {
            state: {
                busInfo: { segment: { ...seg, boarding_points: seg.boarding_points || [], dropping_points: seg.dropping_points || [], bus_test: false } },
                searchParams: { from: seg.origin_name, to: seg.dest_name, date: seg.origin_dep_dt?.split("T")[0], adults: pax, children: 0 },
            },
        });
    };

    const edgeItems = (edge, label) => {
        if (edge.skipped) return null;
        return (edge.route.nodes || []).map((n, i) => {
            const seg = n.segment || {};
            const m = segMode(seg);
            const canBook = !edge.route.estimated && (m === "bus" ? !!(seg.result_idx || seg.trace_id) : !!seg.booking_link);
            return (
                <Item key={label + i} icon={MODE_ICON[m]} title={`${label}: ${segName(seg)}`} sub={`${fmtDateShort(seg.origin_dep_dt)} · ${fmtTime(seg.origin_dep_dt)}`}
                    action={canBook ? (m === "bus" ? () => bookBusSeg(seg) : () => window.open(seg.booking_link, "_blank", "noopener")) : null} />
            );
        });
    };

    return (
        <div style={S.sheetBg} onClick={onClose}>
            <div style={S.sheet} onClick={(e) => e.stopPropagation()}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                        <div style={{ fontWeight: 800, fontSize: 18 }}>{trip.title}</div>
                        <div style={{ fontSize: 13, color: MUTED }}>{fmtDay(inst.day1)} · {pax} traveller{pax > 1 ? "s" : ""} · from {inst.origin.name}</div>
                    </div>
                    <button onClick={onClose} style={S.close} aria-label="Close">×</button>
                </div>

                {paid ? (
                    <div style={{ textAlign: "center", padding: "28px 8px 12px" }}>
                        <div style={{ fontSize: 44 }}>🎉</div>
                        <div style={{ fontWeight: 800, fontSize: 18, marginTop: 8 }}>Trip reserved</div>
                        <div style={{ color: MUTED, fontSize: 14, marginTop: 6, lineHeight: 1.5 }}>
                            Bookings are being confirmed one by one. You'll get a WhatsApp group with {creator.name}'s tips and every ticket.
                        </div>
                    </div>
                ) : (
                    <>
                        <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 6, maxHeight: "44vh", overflowY: "auto" }}>
                            {edgeItems(inst.outbound, "Getting there")}
                            {inst.innerMoves.map((m, i) => <Item key={"m" + i} icon={MODE_ICON[m.mode]} title={m.label} sub={`Day ${m.dayIndex + 1} · dep ${m.depart}`} note="Booked by RouteChef ops" />)}
                            {inst.stays.map((s, i) => <Item key={"s" + i} icon="🏨" title={s.name} sub={`${fmtDateShort(s.checkIn)} to ${fmtDateShort(s.checkOut)} · ${s.rooms} room${s.rooms > 1 ? "s" : ""}`} action={s.hotel ? () => bookHotel(s) : null} />)}
                            {edgeItems(inst.inbound, "Getting back")}
                        </div>

                        <div style={{ borderTop: "1px solid #EEE", marginTop: 12, paddingTop: 12, display: "flex", flexDirection: "column", gap: 6 }}>
                            <div style={S.line}><span style={S.lineLabel}>Travel and stays</span><span>{inr(inst.price.supplier)}</span></div>
                            <div style={S.line}><span style={S.lineLabel}>Itinerary, support and fees</span><span>{inr(inst.price.markup + PLATFORM_FEE + PLATFORM_GST)}</span></div>
                            <div style={{ ...S.line, fontWeight: 800, fontSize: 16, color: INK }}><span>Total</span><span>{inr(inst.price.total)}</span></div>
                        </div>

                        <button onClick={() => setPaid(true)} style={S.bookBtn}>Pay {inr(deposit)} now (25%) →</button>
                        <div style={{ fontSize: 11, color: MUTED, textAlign: "center", marginTop: 8 }}>
                            Demo checkout. Individual items with "Book" go to the live RouteChef booking flow.
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

const Item = ({ icon, title, sub, action, note }) => (
    <div style={S.item}>
        <span style={{ fontSize: 16 }}>{icon}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{title}</div>
            <div style={{ fontSize: 12, color: MUTED }}>{sub}</div>
        </div>
        {action
            ? <button onClick={action} style={S.itemBtn}>Book</button>
            : <span style={{ fontSize: 11, color: MUTED }}>{note || "Included"}</span>}
    </div>
);

/* ── utils + styles ─────────────────────────────────────────────────────── */
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const fmtDateShort = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "";

const S = {
    page: { minHeight: "100vh", background: "#FAFAF9", fontFamily: "'Inter', -apple-system, system-ui, sans-serif", color: INK, paddingBottom: 90 },
    top: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", background: "#fff", borderBottom: "1px solid #EEE" },
    back: { textDecoration: "none", color: MUTED, fontSize: 14, fontWeight: 600 },
    hero: { backgroundSize: "cover", backgroundPosition: "center", minHeight: 300, display: "flex", alignItems: "flex-end" },
    heroInner: { maxWidth: 1080, width: "100%", margin: "0 auto", padding: "28px 16px 70px" },
    creatorChip: { display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,.16)", backdropFilter: "blur(6px)", color: "#fff", fontSize: 12, fontWeight: 600, padding: "5px 12px 5px 5px", borderRadius: 99 },
    miniAvatar: { width: 24, height: 24, borderRadius: "50%", background: ORANGE, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800 },
    reelBtn: { background: "#fff", color: INK, textDecoration: "none", fontSize: 13, fontWeight: 700, padding: "8px 14px", borderRadius: 99 },
    heroTag: { color: "#fff", border: "1px solid rgba(255,255,255,.4)", fontSize: 12, padding: "5px 11px", borderRadius: 99 },
    controlsWrap: { maxWidth: 1080, margin: "-46px auto 0", padding: "0 16px", position: "sticky", top: 8, zIndex: 20 },
    controls: { background: "#fff", borderRadius: 16, boxShadow: "0 12px 34px -14px rgba(15,23,42,.35)", border: "1px solid #EEE", padding: "14px 16px", display: "flex", gap: 14, flexWrap: "wrap" },
    input: { height: 40, border: "1.5px solid #E2E8F0", borderRadius: 10, padding: "0 10px", fontSize: 15, fontWeight: 600, fontFamily: "inherit", background: "#fff", color: INK, width: "100%", boxSizing: "border-box" },
    step: { width: 32, height: 32, borderRadius: "50%", border: "1.5px solid #E2E8F0", background: "#fff", fontSize: 18, cursor: "pointer", lineHeight: 1 },
    chip: { border: "1.5px solid #E2E8F0", background: "#fff", borderRadius: 99, padding: "6px 10px", fontSize: 12, fontWeight: 600, color: MUTED, cursor: "pointer", fontFamily: "inherit" },
    chipOn: { borderColor: ORANGE, color: ORANGE, background: "#FFF4EE" },
    status: { fontSize: 12, color: MUTED, padding: "8px 4px 0", background: "transparent" },
    main: { maxWidth: 1080, margin: "18px auto 0", padding: "0 16px", display: "grid", gridTemplateColumns: "minmax(0, 1fr) 340px", gap: 20, alignItems: "start" },
    section: { background: "#fff", border: "1px solid #EEE", borderRadius: 16, padding: "16px", marginBottom: 12 },
    liveBadge: { fontSize: 10, fontWeight: 800, letterSpacing: 0.6, color: ORANGE, background: "#FFF1EA", padding: "3px 8px", borderRadius: 99, textTransform: "uppercase" },
    edgeCard: { border: "1.5px solid #FFD6C2", background: "#FFFAF7", borderRadius: 12, padding: 12 },
    seg: { display: "flex", alignItems: "center", gap: 10, background: "#fff", borderRadius: 9, padding: "8px 10px", border: "1px solid #F1F5F9" },
    row: { display: "flex", alignItems: "center", gap: 10, border: "1px solid #F1F5F9", borderRadius: 11, padding: "10px 12px" },
    rowIcon: { fontSize: 18, width: 24, textAlign: "center", flexShrink: 0 },
    rowTitle: { fontWeight: 600, fontSize: 14, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" },
    rowSub: { fontSize: 12, color: MUTED, marginTop: 2, lineHeight: 1.45 },
    rowPrice: { fontWeight: 700, fontSize: 13, whiteSpace: "nowrap" },
    tip: { fontSize: 13, background: "#F8FAFC", borderRadius: 10, padding: "9px 12px", color: "#334155", lineHeight: 1.5 },
    lock: { fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 99 },
    lockLocked: { background: "#F3E8FF", color: "#6B3FA0" },
    lockPref: { background: "#E0F2FE", color: "#0369A1" },
    lockFlex: { background: "#ECFDF5", color: GREEN },
    subNote: { fontSize: 13, color: MUTED, background: "#F8FAFC", borderRadius: 10, padding: "9px 12px" },
    warn: { fontSize: 13, background: "#EFF6FF", color: "#1E40AF", borderRadius: 12, padding: "10px 14px", marginBottom: 10 },
    warnSeason: { background: "#FFFBEB", color: AMBER },
    legend: { display: "flex", gap: 14, flexWrap: "wrap", fontSize: 12, color: MUTED, padding: "4px 4px 20px" },
    priceCol: { position: "sticky", top: 130 },
    priceCard: { background: "#fff", border: "1px solid #EEE", borderRadius: 18, padding: 18, boxShadow: "0 10px 30px -18px rgba(15,23,42,.3)" },
    line: { display: "flex", justifyContent: "space-between", gap: 10, fontSize: 13, color: "#334155" },
    lineLabel: { minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    est: { fontSize: 10, fontWeight: 700, color: AMBER, background: "#FEF3C7", padding: "1px 6px", borderRadius: 99, marginLeft: 6 },
    bookBtn: { width: "100%", marginTop: 16, background: ORANGE, color: "#fff", border: "none", borderRadius: 12, padding: "14px 0", fontWeight: 800, fontSize: 15, cursor: "pointer", fontFamily: "inherit", boxShadow: "0 8px 22px -8px rgba(255,91,21,.6)" },
    mobileBar: { position: "fixed", left: 0, right: 0, bottom: 0, background: "#fff", borderTop: "1px solid #EEE", padding: "10px 16px", display: "none", justifyContent: "space-between", alignItems: "center", zIndex: 30 },
    sheetBg: { position: "fixed", inset: 0, background: "rgba(15,23,42,.45)", zIndex: 50, display: "flex", alignItems: "flex-end", justifyContent: "center" },
    sheet: { background: "#fff", width: "100%", maxWidth: 560, borderRadius: "20px 20px 0 0", padding: "18px 18px 22px", boxSizing: "border-box", maxHeight: "92vh", overflowY: "auto" },
    close: { width: 34, height: 34, borderRadius: "50%", border: "none", background: "#F1F5F9", fontSize: 20, cursor: "pointer" },
    item: { display: "flex", alignItems: "center", gap: 10, border: "1px solid #F1F5F9", borderRadius: 10, padding: "9px 11px" },
    itemBtn: { border: "none", background: "#FFF1EA", color: ORANGE, fontWeight: 700, fontSize: 12, padding: "6px 12px", borderRadius: 99, cursor: "pointer", fontFamily: "inherit" },
    changeBtn: { border: "1.5px solid #FFD6C2", background: "#fff", color: ORANGE, fontWeight: 700, fontSize: 12, padding: "6px 12px", borderRadius: 99, cursor: "pointer", fontFamily: "inherit" },
    bestPick: { display: "inline-block", fontSize: 10, fontWeight: 800, letterSpacing: 0.5, textTransform: "uppercase", color: "#fff", background: ORANGE, padding: "2px 7px", borderRadius: 99, marginRight: 8, verticalAlign: "middle" },
    mapBtn: { width: 34, height: 34, borderRadius: 10, border: "1.5px solid #FFD6C2", background: "#fff", color: ORANGE, display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 },
    mapBtnSm: { width: 30, height: 30, borderRadius: 8, border: "1px solid #E2E8F0", background: "#fff", color: MUTED, display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 },
    modeTabs: { display: "flex", gap: 6, marginTop: 10, overflowX: "auto", paddingBottom: 2 },
    modeTab: { display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 1, border: "1.5px solid #E2E8F0", background: "#fff", borderRadius: 10, padding: "6px 10px", cursor: "pointer", fontFamily: "inherit", color: INK, whiteSpace: "nowrap", flexShrink: 0 },
    modeTabOn: { borderColor: ORANGE, background: "#FFF4EE", color: ORANGE },
    optList: { display: "flex", flexDirection: "column", gap: 6, marginTop: 10, maxHeight: 360, overflowY: "auto", paddingRight: 2 },
    opt: { display: "flex", alignItems: "center", gap: 10, textAlign: "left", width: "100%", background: "#fff", border: "1.5px solid #EEF2F7", borderRadius: 10, padding: "9px 11px", cursor: "pointer", fontFamily: "inherit", color: INK },
    optOn: { borderColor: ORANGE, background: "#FFF4EE" },
};

const CSS = `
@keyframes ctPulse{0%,100%{opacity:1}50%{opacity:.4}}
.ct-pulse{animation:ctPulse 1.1s ease-in-out infinite;color:${ORANGE};font-weight:600}
@media (max-width: 860px){
  .ct-main{grid-template-columns:minmax(0,1fr) !important}
  .ct-main aside{position:static !important}
  .ct-mobilebar{display:flex !important}
}
`;
